"use client";

import { useEffect, useState, useMemo } from "react";

export interface DeviceInfo {
  device: "mobile" | "tablet" | "desktop";
  browser: "chrome" | "safari" | "firefox" | "edge" | "other";
  platform: "ios" | "android" | "windows" | "macos" | "linux" | "other";
  isLandscape: boolean;
  viewportWidth: number;
  viewportHeight: number;
}

export function useDeviceDetection(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    if (typeof window === "undefined") {
      return getServerSideDefaults();
    }
    return detectDevice();
  });

  useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(detectDevice());
    };

    const handleOrientationChange = () => {
      // Add a small delay to ensure orientation change is complete
      setTimeout(() => {
        setDeviceInfo(detectDevice());
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  return deviceInfo;
}

// Convenience hooks for common use cases
export function useIsMobile(): boolean {
  const { device } = useDeviceDetection();
  return useMemo(() => device === "mobile", [device]);
}

export function useIsTablet(): boolean {
  const { device } = useDeviceDetection();
  return useMemo(() => device === "tablet", [device]);
}

export function useIsDesktop(): boolean {
  const { device } = useDeviceDetection();
  return useMemo(() => device === "desktop", [device]);
}

function detectDevice(): DeviceInfo {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const userAgent = navigator.userAgent;

  // Device type detection (using consistent 768px breakpoint)
  const device =
    width < 768
      ? "mobile"
      : width >= 768 && width < 1024
        ? "tablet"
        : "desktop";

  // Orientation detection
  const isLandscape = width > height;

  // OS detection
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);
  const isMacOS = /Mac/.test(userAgent) && !isIOS;
  const isWindows = /Windows/.test(userAgent);

  // Browser detection
  const browser = getBrowserName(userAgent);
  const platform = getPlatformName(
    userAgent,
    isIOS,
    isAndroid,
    isMacOS,
    isWindows
  );

  return {
    device,
    browser,
    platform,
    isLandscape,
    viewportWidth: width,
    viewportHeight: height,
  };
}

function getBrowserName(
  userAgent: string
): "chrome" | "safari" | "firefox" | "edge" | "other" {
  // Check for Chrome first (including when emulating other devices)
  if (userAgent.includes("Chrome") && !userAgent.includes("Edg"))
    return "chrome";
  // Check for Edge
  if (userAgent.includes("Edg")) return "edge";
  // Check for Firefox
  if (userAgent.includes("Firefox")) return "firefox";
  // Check for Safari (only if not Chrome/Edge/Firefox)
  if (userAgent.includes("Safari")) return "safari";
  return "other";
}

function getPlatformName(
  userAgent: string,
  isIOS: boolean,
  isAndroid: boolean,
  isMacOS: boolean,
  isWindows: boolean
): "ios" | "android" | "windows" | "macos" | "linux" | "other" {
  if (isIOS) return "ios";
  if (isAndroid) return "android";
  if (isWindows) return "windows";
  if (isMacOS) return "macos";
  if (userAgent.includes("Linux")) return "linux";
  return "other";
}

function getServerSideDefaults(): DeviceInfo {
  return {
    device: "desktop",
    browser: "other",
    platform: "other",
    isLandscape: true,
    viewportWidth: 1024,
    viewportHeight: 768,
  };
}
